import type { AcaoInterfaceMia, AcaoMia, BlocoMia, LinhaMia, TipoRegistroMia } from './assistenteService';

/**
 * Le os blocos que a API manda (contracts/schemas/assistente-bloco.schema.json).
 * Tipo desconhecido ou bloco torto some; acao so com caminho interno.
 */
export function caminhoInterno(path: string): boolean {
  return path.startsWith('/') && !path.startsWith('//') && !path.includes('://') && !path.includes('\\');
}

function objeto(valor: unknown): Record<string, unknown> | null {
  return valor && typeof valor === 'object' && !Array.isArray(valor) ? (valor as Record<string, unknown>) : null;
}

function texto(valor: unknown): string | undefined {
  return typeof valor === 'string' && valor.trim() ? valor : undefined;
}

export function lerAcao(valor: unknown): AcaoMia | null {
  const no = objeto(valor);
  const rotulo = texto(no?.rotulo);
  if (!no || !rotulo) return null;
  if (no.tipo === 'navegar') {
    const path = texto(no.path);
    return path && caminhoInterno(path) ? { tipo: 'navegar', rotulo, path } : null;
  }
  if (no.tipo === 'perguntar') {
    const fala = texto(no.texto);
    return fala ? { tipo: 'perguntar', rotulo, texto: fala } : null;
  }
  return null;
}

const TIPOS_REGISTRO: TipoRegistroMia[] = ['recado', 'acesso', 'saude', 'soninho', 'alimentacao', 'higiene'];

/** Acao de interface: so caminho interno, alvo no formato de data-alvo e ids positivos. */
export function lerAcaoInterface(valor: unknown): AcaoInterfaceMia | null {
  const no = objeto(valor);
  const rotulo = texto(no?.rotulo) ?? '';
  if (!no) return null;
  switch (no.tipo) {
    case 'navegar': {
      const path = texto(no.path);
      return path && caminhoInterno(path) ? { tipo: 'navegar', rotulo, path } : null;
    }
    case 'destacar': {
      const alvo = texto(no.alvo);
      return alvo && /^[a-z0-9-]{1,40}$/.test(alvo) ? { tipo: 'destacar', rotulo, alvo } : null;
    }
    case 'abrir_registro': {
      const path = texto(no.path);
      const registro = objeto(no.registro);
      const tipo = TIPOS_REGISTRO.find((t) => t === registro?.tipo);
      const idAluno = typeof registro?.idAluno === 'number' && registro.idAluno > 0 ? registro.idAluno : null;
      if (!path || !caminhoInterno(path) || !tipo || idAluno === null) return null;
      return {
        tipo: 'abrir_registro',
        rotulo,
        path,
        registro: {
          tipo,
          idAluno,
          nomeAluno: texto(registro?.nomeAluno),
          titulo: texto(registro?.titulo),
          texto: texto(registro?.texto),
        },
      };
    }
    case 'trocar_escola': {
      const idEscola = typeof no.idEscola === 'number' && no.idEscola > 0 ? no.idEscola : null;
      return idEscola === null ? null : { tipo: 'trocar_escola', rotulo, idEscola };
    }
    default:
      return null;
  }
}

function lerLinhas(valor: unknown): LinhaMia[] {
  if (!Array.isArray(valor)) return [];
  return valor.flatMap((item) => {
    const no = objeto(item);
    const rotulo = texto(no?.rotulo);
    const conteudo = texto(no?.valor);
    return rotulo && conteudo ? [{ rotulo, valor: conteudo, destaque: no?.destaque === true }] : [];
  });
}

function lerAcoes(valor: unknown): AcaoMia[] {
  if (!Array.isArray(valor)) return [];
  return valor.map(lerAcao).filter((acao): acao is AcaoMia => acao !== null);
}

function lerBloco(valor: unknown): BlocoMia | null {
  const no = objeto(valor);
  if (!no) return null;
  switch (no.tipo) {
    case 'cartao_pessoa': {
      const nome = texto(no.nome);
      const pessoa = no.pessoa;
      if (!nome || (pessoa !== 'ALUNO' && pessoa !== 'RESPONSAVEL' && pessoa !== 'COLABORADOR')) return null;
      return {
        tipo: 'cartao_pessoa',
        pessoa,
        nome,
        foto: texto(no.foto),
        selo: no.selo === 'ANIVERSARIO' ? 'ANIVERSARIO' : undefined,
        linhas: lerLinhas(no.linhas),
        acoes: lerAcoes(no.acoes),
      };
    }
    case 'lista': {
      if (!Array.isArray(no.itens)) return null;
      const itens = no.itens.flatMap((item) => {
        const linha = objeto(item);
        const titulo = texto(linha?.titulo);
        if (!linha || !titulo) return [];
        return [{
          titulo,
          subtitulo: texto(linha.subtitulo),
          foto: texto(linha.foto),
          acao: lerAcao(linha.acao) ?? undefined,
        }];
      });
      if (itens.length === 0) return null;
      return {
        tipo: 'lista',
        titulo: texto(no.titulo),
        total: typeof no.total === 'number' ? no.total : undefined,
        itens,
      };
    }
    case 'resumo': {
      const itens = lerLinhas(no.itens);
      return itens.length ? { tipo: 'resumo', titulo: texto(no.titulo), itens, rodape: texto(no.rodape) } : null;
    }
    case 'tabela': {
      if (!Array.isArray(no.colunas) || !Array.isArray(no.linhas)) return null;
      const colunas = no.colunas.map((c) => (typeof c === 'string' ? c : ''));
      const linhas = no.linhas
        .filter(Array.isArray)
        .map((linha: unknown[]) => linha.map((c) => (typeof c === 'string' ? c : '')));
      return colunas.length ? { tipo: 'tabela', titulo: texto(no.titulo), colunas, linhas } : null;
    }
    case 'botoes': {
      const acoes = lerAcoes(no.acoes);
      return acoes.length ? { tipo: 'botoes', acoes } : null;
    }
    case 'sugestoes': {
      if (!Array.isArray(no.itens)) return null;
      const itens = no.itens.map(texto).filter((item): item is string => Boolean(item));
      return itens.length ? { tipo: 'sugestoes', itens } : null;
    }
    case 'resultado': {
      const titulo = texto(no.titulo);
      return titulo
        ? { tipo: 'resultado', titulo, texto: texto(no.texto), acao: lerAcao(no.acao) ?? undefined }
        : null;
    }
    case 'imagens': {
      if (!Array.isArray(no.itens)) return null;
      const itens = no.itens.flatMap((item) => {
        const imagem = objeto(item);
        const url = texto(imagem?.url);
        return url && /^https:\/\//.test(url) ? [{ url, legenda: texto(imagem?.legenda) }] : [];
      });
      return itens.length ? { tipo: 'imagens', itens } : null;
    }
    case 'acao_interface': {
      const acao = lerAcaoInterface(no.acao);
      return acao ? { tipo: 'acao_interface', acao } : null;
    }
    default:
      return null;
  }
}

export function lerBlocos(valor: unknown): BlocoMia[] {
  if (!Array.isArray(valor)) return [];
  return valor.slice(0, 12).map(lerBloco).filter((bloco): bloco is BlocoMia => bloco !== null);
}
